import { useState, useMemo } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { useLang } from "@/lib/i18n";

const getOffset = (tz) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "shortOffset" }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value || "";
  } catch {
    return "";
  }
};

export const TimezoneSelect = ({ value, onChange, testId = "timezone-select" }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);

  const zones = useMemo(() => {
    let list;
    try {
      list = Intl.supportedValuesOf("timeZone");
    } catch {
      list = ["UTC", "Europe/Paris", "Europe/London", "America/New_York", "Asia/Tehran", "Asia/Dubai"];
    }
    return list.map((tz) => ({ tz, offset: getOffset(tz) }));
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} data-testid={testId} className="w-full justify-between font-normal">
          <span className="truncate">{value ? `${value.replace(/_/g, " ")} (${getOffset(value)})` : t("timezone")}</span>
          <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0 ml-2" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="start">
        <Command>
          <CommandInput placeholder={t("timezone_search")} data-testid={`${testId}-search`} />
          <CommandList>
            <CommandEmpty>{t("no_results")}</CommandEmpty>
            <CommandGroup>
              {zones.map(({ tz, offset }) => (
                <CommandItem
                  key={tz}
                  value={tz}
                  data-testid={`${testId}-option`}
                  onSelect={() => { onChange(tz); setOpen(false); }}
                >
                  <Check className={`h-4 w-4 mr-2 shrink-0 ${value === tz ? "opacity-100" : "opacity-0"}`} />
                  <span className="truncate flex-1">{tz.replace(/_/g, " ")}</span>
                  <span className="text-xs font-mono-data text-muted-foreground ml-2 shrink-0">{offset}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
